import { useEffect, useRef, useState, type ReactNode } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router";
import { toast } from "sonner";
import * as api from "@/api/endpoints";
import { useApplication, useCandidate, useCandidates } from "@/api/hooks/admin";
import {
  useArchiveCandidate,
  useUpdateCandidate,
  useUploadCandidateCv,
} from "@/api/hooks/mutations";
import type {
  Candidate,
  CandidateConsentStatus,
  CandidateRepresentationStatus,
  CandidateUpdateInput,
} from "@/api/endpoints";
import { ApiError } from "@/api/client";
import { Button, buttonClass } from "@/components/ui/Button";
import { ChipInput } from "@/components/ui/ChipInput";
import { FileUploader } from "@/components/ui/FileUploader";
import { PaginationControls } from "@/components/ui/PaginationControls";
import { PillTabs } from "@/components/ui/PillTabs";
import { StatusTag } from "@/components/ui/StatusTag";
import { fmtDate, humanize, initials } from "@/lib/format";
import { safeUrl } from "@/lib/safe-url";

type CandidateStatus = CandidateRepresentationStatus;
type CandidateSourceFilter = "all" | "sourced_profile" | "application" | "manual" | "role_linked" | "unassigned";

type CandidateDraft = {
  name: string;
  linkedinUrl: string;
  telegramUrl: string;
  email: string;
  location: string;
  geo: string;
  currentTitle: string;
  currentCompany: string;
  expectedRate: string;
  availability: string;
  minimumRate: string;
  rateType: string;
  currency: string;
  englishLevel: string;
  noticePeriod: string;
  earliestStart: string;
  workAuthorization: string;
  safeToShareNotes: string;
  consentScope: string;
  profileStatus: string;
  profileHeadline: string;
  profileSummary: string;
  profileRoleTitle: string;
  profileSkills: string[];
  profileExperience: string[];
  cvUrl: string;
  coreStack: string[];
  notes: string;
  status: CandidateStatus;
  consent: CandidateConsentStatus;
};

const STATUS_OPTIONS = [
  { value: "all" as const, label: "All" },
  { value: "shortlist_ready" as const, label: "Shortlisted" },
  { value: "outreach_pending" as const, label: "Outreach pending" },
  { value: "contacted" as const, label: "Contacted" },
  { value: "replied" as const, label: "Replied" },
  { value: "screening" as const, label: "Screening" },
  { value: "submitted" as const, label: "Submitted" },
  { value: "rejected" as const, label: "Rejected" },
  { value: "represented" as const, label: "Represented" },
  { value: "hold" as const, label: "Hold" },
  { value: "archived" as const, label: "Archived" },
];

type StatusValue = (typeof STATUS_OPTIONS)[number]["value"];

const SOURCE_OPTIONS: Array<{ value: CandidateSourceFilter; label: string }> = [
  { value: "all", label: "All sources" },
  { value: "sourced_profile", label: "LinkedIn sourced" },
  { value: "application", label: "Website applicants" },
  { value: "manual", label: "Manual records" },
  { value: "role_linked", label: "Linked to a role" },
  { value: "unassigned", label: "Unassigned pool" },
];

export function AdminCandidatesPage() {
  const { id } = useParams();
  const detailId = id ? parseInt(id, 10) : null;

  if (detailId != null) {
    return <CandidateProcessingPage id={detailId} />;
  }

  return <CandidatePoolPage />;
}

function CandidatePoolPage() {
  const [searchParams, setSearchParams] = useSearchParams();

  const status = (searchParams.get("status") as StatusValue) ?? "all";
  const source = (searchParams.get("source") as CandidateSourceFilter) ?? "all";
  const q = searchParams.get("q") ?? "";
  const page = parseInt(searchParams.get("page") ?? "1", 10);

  const list = useCandidates({ status, source, q: q || undefined, page, page_size: 20 });

  const [searchDraft, setSearchDraft] = useState(q);
  useEffect(() => setSearchDraft(q), [q]);
  const debounceRef = useRef<number | null>(null);

  function onSearch(v: string) {
    setSearchDraft(v);
    if (debounceRef.current) window.clearTimeout(debounceRef.current);
    debounceRef.current = window.setTimeout(() => {
      const next = new URLSearchParams(searchParams);
      next.delete("page");
      if (v) next.set("q", v);
      else next.delete("q");
      setSearchParams(next, { replace: true });
    }, 300);
  }

  function setStatus(s: StatusValue) {
    const next = new URLSearchParams(searchParams);
    next.delete("page");
    if (s === "all") next.delete("status");
    else next.set("status", s);
    setSearchParams(next, { replace: true });
  }

  function setSourceFilter(s: CandidateSourceFilter) {
    const next = new URLSearchParams(searchParams);
    next.delete("page");
    if (s === "all") next.delete("source");
    else next.set("source", s);
    setSearchParams(next, { replace: true });
  }

  function setPage(nextPage: number) {
    const next = new URLSearchParams(searchParams);
    if (nextPage <= 1) next.delete("page");
    else next.set("page", String(nextPage));
    setSearchParams(next, { replace: true });
  }

  const visibleCandidates = list.data?.items ?? [];
  const poolStats = summarizeVisiblePool(visibleCandidates);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold tracking-tight">Candidates</h1>
          <p className="mt-1 text-sm text-text-muted">
            Reusable talent pool across sourced profiles, website applicants, and manual records.
          </p>
        </div>
        <input
          type="text"
          value={searchDraft}
          onChange={(e) => onSearch(e.target.value)}
          placeholder="Search name, title, location…"
          className="w-full rounded-md border border-border bg-surface px-3.5 py-2 text-sm outline-none focus:border-primary sm:w-72"
        />
      </div>

      <div className="grid gap-3 md:grid-cols-4">
        <PoolMetric label="Total matches" value={list.isLoading ? "…" : String(list.data?.total ?? 0)} />
        <PoolMetric label="Role-linked visible" value={String(poolStats.roleLinked)} />
        <PoolMetric label="Sourced visible" value={String(poolStats.sourced)} />
        <PoolMetric label="Applicants visible" value={String(poolStats.applications)} />
      </div>

      <div className="grid gap-3 lg:grid-cols-[minmax(0,1fr)_260px]">
        <div className="min-w-0 overflow-hidden">
          <PillTabs value={status} onChange={setStatus} options={STATUS_OPTIONS} />
        </div>
        <label className="block">
          <span className="mb-1.5 block text-xs font-semibold uppercase tracking-wider text-text-muted">Pool filter</span>
          <select
            aria-label="Candidate source filter"
            value={source}
            onChange={(e) => setSourceFilter(e.target.value as CandidateSourceFilter)}
            className="w-full rounded-md border border-border bg-surface px-3 py-2 text-sm"
          >
            {SOURCE_OPTIONS.map((option) => (
              <option key={option.value} value={option.value}>{option.label}</option>
            ))}
          </select>
        </label>
      </div>

      <div className="flex flex-col gap-3 md:hidden">
        {list.isLoading ? (
          <MobileEmptyState>Loading…</MobileEmptyState>
        ) : !list.data?.items.length ? (
          <MobileEmptyState>No candidates match.</MobileEmptyState>
        ) : (
          list.data.items.map((candidate) => <CandidateCard key={candidate.id} row={candidate} />)
        )}
      </div>

      <div className="hidden overflow-x-auto rounded-2xl border border-border bg-surface md:block">
        <table aria-label="Candidates table" className="min-w-[960px] w-full border-separate border-spacing-0 text-[13px]">
          <thead>
            <tr className="bg-surface-alt text-left text-[12px] font-semibold uppercase tracking-wider text-text-muted">
              <th className="px-4 py-3.5">Candidate</th>
              <th className="px-4 py-3.5">Source</th>
              <th className="px-4 py-3.5">Current role</th>
              <th className="px-4 py-3.5">Location</th>
              <th className="px-4 py-3.5">Lifecycle</th>
              <th className="px-4 py-3.5">Rate</th>
              <th className="px-4 py-3.5">Updated</th>
              <th className="px-4 py-3.5"></th>
            </tr>
          </thead>
          <tbody>
            {list.isLoading ? (
              <tr>
                <td colSpan={8} className="p-8 text-center text-text-muted">
                  Loading…
                </td>
              </tr>
            ) : !list.data?.items.length ? (
              <tr>
                <td colSpan={8} className="p-8 text-center text-text-muted">
                  No candidates match.
                </td>
              </tr>
            ) : (
              list.data.items.map((candidate) => <CandidateRow key={candidate.id} row={candidate} />)
            )}
          </tbody>
        </table>
      </div>

      {list.data && list.data.total > list.data.page_size ? (
        <PaginationControls
          page={list.data.page}
          pageSize={list.data.page_size}
          total={list.data.total}
          itemLabel="candidates"
          onPageChange={setPage}
        />
      ) : null}
    </div>
  );
}

function CandidateRow({ row }: { row: Candidate }) {
  const source = candidateSource(row);
  return (
    <tr className="border-t border-border hover:bg-surface-alt">
      <td className="px-4 py-3.5">
        <div className="flex items-center gap-3">
          <div className="grid size-9 place-items-center rounded-full bg-gradient-to-br from-success to-primary text-[13px] font-bold text-white">
            {initials(row.name, row.email)}
          </div>
          <div className="min-w-0">
            <div className="break-words font-medium">{row.name}</div>
            <div className="break-all text-text-muted">{row.linkedin_url || row.email || "No external link"}</div>
          </div>
        </div>
      </td>
      <td className="px-4 py-3.5">
        <div className="flex flex-col gap-1">
          <StatusTag status={source.status} label={source.label} />
          {row.role_brief_id ? (
            <Link to={`/admin/role-rail/${row.role_brief_id}`} className="text-xs text-primary hover:underline">
              Role #{row.role_brief_id}
            </Link>
          ) : (
            <span className="text-xs text-text-muted">Reusable pool</span>
          )}
        </div>
      </td>
      <td className="px-4 py-3.5 text-text-muted">{row.current_title || "—"}</td>
      <td className="px-4 py-3.5 text-text-muted">{row.location || row.geo || "—"}</td>
      <td className="px-4 py-3.5">
        <StatusTag status={row.representation_status} />
      </td>
      <td className="px-4 py-3.5">{row.expected_rate || "—"}</td>
      <td className="px-4 py-3.5 text-text-muted">{fmtDate(row.updated_at)}</td>
      <td className="px-4 py-3.5">
        <Link to={"/admin/candidates/" + row.id} className={buttonClass({ variant: "ghost", size: "sm" })}>
          Open
        </Link>
      </td>
    </tr>
  );
}

function CandidateCard({ row }: { row: Candidate }) {
  const source = candidateSource(row);
  return (
    <article className="rounded-2xl border border-border bg-surface p-4">
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <div className="font-semibold">{row.name}</div>
          <div className="mt-1 break-words text-sm text-text-muted">{row.current_title || "Title missing"}</div>
        </div>
        <StatusTag status={row.representation_status} />
      </div>
      <div className="mt-3 grid grid-cols-1 gap-2 text-sm text-text-muted">
        <div className="flex flex-wrap gap-2">
          <StatusTag status={source.status} label={source.label} />
          {row.role_brief_id ? (
            <Link to={`/admin/role-rail/${row.role_brief_id}`} className="text-primary hover:underline">
              Role #{row.role_brief_id}
            </Link>
          ) : (
            <span>Reusable pool</span>
          )}
        </div>
        <div>{row.location || row.geo || "Location missing"}</div>
        <div>{row.expected_rate || "Rate not set"}</div>
        <div>{fmtDate(row.updated_at)}</div>
      </div>
      <Link
        to={"/admin/candidates/" + row.id}
        className={`${buttonClass({ variant: "ghost", size: "sm" })} mt-4 w-full justify-center`}
      >
        Open
      </Link>
    </article>
  );
}

function UrlValue({ value }: { value?: string | null }) {
  if (!value) return <>—</>;
  const safe = safeUrl(value);
  if (!safe) return <>{value}</>;
  return (
    <a href={safe} target="_blank" rel="noopener noreferrer" className="text-primary hover:underline">
      {value}
    </a>
  );
}

function CandidateUrlButton({ value, label }: { value?: string | null; label: string }) {
  const safe = safeUrl(value);
  if (!safe) {
    return <p className="text-sm text-text-muted">{value || "No URL available."}</p>;
  }
  return (
    <a href={safe} target="_blank" rel="noopener noreferrer" className={buttonClass({ variant: "secondary", size: "sm" })}>
      {label}
    </a>
  );
}

function CandidateProcessingPage({ id }: { id: number }) {
  const navigate = useNavigate();
  const detail = useCandidate(id);
  const sourceApplication = useApplication(detail.data?.source_application_id ?? null);
  const update = useUpdateCandidate();
  const uploadCv = useUploadCandidateCv();
  const archive = useArchiveCandidate();

  const [draft, setDraft] = useState<CandidateDraft | null>(null);
  const [pendingCvFile, setPendingCvFile] = useState<File | null>(null);
  const [confirmAction, setConfirmAction] = useState<CleanupAction>(null);

  useEffect(() => {
    if (!detail.data) return;
    setDraft(createCandidateDraft(detail.data));
    setPendingCvFile(null);
  }, [detail.data?.updated_at, id]);

  if (detail.isLoading) {
    return (
      <section className="rounded-2xl border border-border bg-surface p-6 text-text-muted">
        Loading…
      </section>
    );
  }

  if (detail.error) {
    return (
      <section className="rounded-2xl border border-border bg-surface p-6 text-danger">
        {detail.error instanceof ApiError && detail.error.status === 404
          ? "This candidate no longer exists."
          : detail.error instanceof Error
            ? detail.error.message
            : "Could not load this candidate."}
      </section>
    );
  }
  if (!detail.data) {
    return (
      <section className="rounded-2xl border border-border bg-surface p-6 text-text-muted">
        This candidate is unavailable.
      </section>
    );
  }

  if (!draft) {
    return (
      <section className="rounded-2xl border border-border bg-surface p-6 text-text-muted">
        Loading…
      </section>
    );
  }

  const candidate = detail.data;
  const source = candidateSource(candidate);
  const cvHref = candidate.cv_blob_id ? api.blobUrl(candidate.cv_blob_id) : null;
  const shareState = computeShareState(draft, candidate, pendingCvFile);
  const sourceSnippets = collectSourceSnippets(sourceApplication.data);

  function patchDraft<K extends keyof CandidateDraft>(key: K, value: CandidateDraft[K]) {
    setDraft((current) => (current ? { ...current, [key]: value } : current));
  }

  function resetDraft() {
    setDraft(createCandidateDraft(candidate));
    setPendingCvFile(null);
  }

  async function onSave() {
    const currentDraft = draft;
    if (!currentDraft) return;
    try {
      const body: CandidateUpdateInput = {
        name: normalizeOptional(currentDraft.name),
        linkedin_url: normalizeOptional(currentDraft.linkedinUrl),
        telegram_url: normalizeOptional(currentDraft.telegramUrl),
        email: normalizeOptional(currentDraft.email),
        location: normalizeOptional(currentDraft.location),
        geo: normalizeOptional(currentDraft.geo),
        current_title: normalizeOptional(currentDraft.currentTitle),
        current_company: normalizeOptional(currentDraft.currentCompany),
        expected_rate: normalizeOptional(currentDraft.expectedRate),
        availability: normalizeOptional(currentDraft.availability),
        minimum_rate: normalizeInteger(currentDraft.minimumRate),
        rate_type: normalizeOptional(currentDraft.rateType),
        currency: normalizeOptional(currentDraft.currency),
        english_level: normalizeOptional(currentDraft.englishLevel),
        notice_period: normalizeOptional(currentDraft.noticePeriod),
        earliest_start: normalizeOptional(currentDraft.earliestStart),
        work_authorization: normalizeOptional(currentDraft.workAuthorization),
        safe_to_share_notes: normalizeOptional(currentDraft.safeToShareNotes),
        consent_scope: normalizeOptional(currentDraft.consentScope),
        profile_status: normalizeOptional(currentDraft.profileStatus),
        profile_headline: normalizeOptional(currentDraft.profileHeadline),
        profile_summary: normalizeOptional(currentDraft.profileSummary),
        profile_role_title: normalizeOptional(currentDraft.profileRoleTitle),
        profile_skills: currentDraft.profileSkills,
        profile_experience: currentDraft.profileExperience,
        cv_url: normalizeOptional(currentDraft.cvUrl),
        core_stack: currentDraft.coreStack,
        notes: currentDraft.notes.trim() || null,
        representation_status: currentDraft.status,
        consent_status: currentDraft.consent,
      };
      await update.mutateAsync({ id, body });
      toast.success("Candidate updated");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Update failed");
    }
  }

  async function onUploadCv() {
    if (!pendingCvFile) return;
    const formData = new FormData();
    formData.set("cv", pendingCvFile);
    try {
      await uploadCv.mutateAsync({ id, formData });
      setPendingCvFile(null);
      toast.success("Candidate CV uploaded");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "CV upload failed");
    }
  }

  async function onArchive() {
    try {
      await archive.mutateAsync(id);
      toast.success("Candidate archived");
      navigate("/admin/candidates");
    } catch (err) {
      toast.error(err instanceof ApiError ? err.message : "Archive failed");
    }
  }

  return (
    <div className="flex flex-col gap-5">
      <div className="flex flex-wrap items-start justify-between gap-4">
        <div className="min-w-0">
          <button
            type="button"
            onClick={() => navigate("/admin/candidates")}
            className={buttonClass({ variant: "ghost", size: "sm" })}
          >
            Back to candidates
          </button>
          <div className="mt-3 flex items-start gap-3">
            <div className="grid size-12 shrink-0 place-items-center rounded-2xl bg-gradient-to-br from-success to-primary text-base font-bold text-white">
              {initials(draft.name, draft.email)}
            </div>
            <div className="min-w-0">
              <h1 className="break-words text-2xl font-bold tracking-tight">
                Candidate: {draft.name || candidate.name}
              </h1>
              <p className="mt-1 text-sm text-text-muted">
                Processing page for reusable candidate facts, consent, CV assets, and source provenance.
              </p>
              <div className="mt-3 flex flex-wrap gap-2">
                <StatusTag status={draft.status} />
                <StatusTag status={source.status} label={source.label} />
                {candidate.role_brief_id ? (
                  <ProvenanceChip label={`Role #${candidate.role_brief_id}`} tone="success" />
                ) : (
                  <ProvenanceChip label="Reusable pool" />
                )}
                <ProvenanceChip label={shareState.label} tone={shareState.tone} />
              </div>
            </div>
          </div>
        </div>
        <div className="flex w-full flex-col gap-2 sm:w-auto sm:flex-row sm:flex-wrap">
          <Button
            variant="secondary"
            onClick={resetDraft}
            disabled={update.isPending || uploadCv.isPending}
          >
            Reset draft
          </Button>
          <Button
            onClick={onSave}
            loading={update.isPending}
            loadingLabel="Saving candidate…"
            disabled={uploadCv.isPending}
          >
            Save candidate
          </Button>
        </div>
      </div>

      {confirmAction && (
        <CleanupConfirm
          noun="candidate"
          itemName={draft.name || candidate.name}
          loading={archive.isPending}
          onCancel={() => setConfirmAction(null)}
          onConfirm={onArchive}
        />
      )}

      <div className="grid gap-5 xl:grid-cols-[minmax(0,1.3fr)_360px]">
        <div className="flex min-w-0 flex-col gap-5">
          <ProcessingSection
            title="Identity and contact"
            description="Normalized contact details that recruiters will actually search and reuse."
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <EditablePair
                label="Name"
                value={
                  <input
                    aria-label="Candidate name"
                    value={draft.name}
                    onChange={(e) => patchDraft("name", e.target.value)}
                    className={inputClass}
                  />
                }
              />
              <EditablePair
                label="Email"
                value={
                  <input
                    aria-label="Candidate email"
                    value={draft.email}
                    onChange={(e) => patchDraft("email", e.target.value)}
                    className={inputClass}
                  />
                }
              />
              <EditablePair
                label="LinkedIn"
                value={
                  <input
                    aria-label="Candidate LinkedIn URL"
                    value={draft.linkedinUrl}
                    onChange={(e) => patchDraft("linkedinUrl", e.target.value)}
                    className={inputClass}
                  />
                }
              />
              <EditablePair
                label="Telegram"
                value={
                  <input
                    aria-label="Candidate Telegram URL"
                    value={draft.telegramUrl}
                    onChange={(e) => patchDraft("telegramUrl", e.target.value)}
                    className={inputClass}
                  />
                }
              />
            </div>
            {(sourceApplication.data?.phone || sourceApplication.data?.github_url) ? (
              <div className="grid gap-3 sm:grid-cols-2">
                {sourceApplication.data?.phone ? (
                  <ReadOnlyFact label="Source application phone" value={sourceApplication.data.phone} />
                ) : null}
                {sourceApplication.data?.github_url ? (
                  <ReadOnlyFact
                    label="Source application GitHub"
                    value={<UrlValue value={sourceApplication.data.github_url} />}
                  />
                ) : null}
              </div>
            ) : null}
          </ProcessingSection>

          <ProcessingSection
            title="Current role and company"
            description="Keep the canonical title, company, and geo fields tidy even when the raw source is noisy."
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <EditablePair
                label="Current title"
                value={
                  <input
                    aria-label="Candidate current title"
                    value={draft.currentTitle}
                    onChange={(e) => patchDraft("currentTitle", e.target.value)}
                    className={inputClass}
                  />
                }
              />
              <EditablePair
                label="Current company"
                value={
                  <input
                    aria-label="Candidate current company"
                    value={draft.currentCompany}
                    onChange={(e) => patchDraft("currentCompany", e.target.value)}
                    className={inputClass}
                  />
                }
              />
              <EditablePair
                label="Location"
                value={
                  <input
                    aria-label="Candidate location"
                    value={draft.location}
                    onChange={(e) => patchDraft("location", e.target.value)}
                    className={inputClass}
                  />
                }
              />
              <EditablePair
                label="Geo"
                value={
                  <input
                    aria-label="Candidate geo"
                    value={draft.geo}
                    onChange={(e) => patchDraft("geo", e.target.value)}
                    className={inputClass}
                  />
                }
              />
            </div>
            {sourceApplication.data?.current_role ? (
              <ReadOnlyFact
                label="Source application current role"
                value={sourceApplication.data.current_role}
              />
            ) : null}
          </ProcessingSection>

          <ProcessingSection
            title="Normalized summary"
            description="Recruiter-editable normalized profile that sits above raw application or sourced text."
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <EditablePair
                label="Profile headline"
                value={
                  <input
                    aria-label="Candidate profile headline"
                    value={draft.profileHeadline}
                    onChange={(e) => patchDraft("profileHeadline", e.target.value)}
                    className={inputClass}
                  />
                }
              />
              <EditablePair
                label="Profile role title"
                value={
                  <input
                    aria-label="Candidate profile role title"
                    value={draft.profileRoleTitle}
                    onChange={(e) => patchDraft("profileRoleTitle", e.target.value)}
                    className={inputClass}
                  />
                }
              />
            </div>
            <textarea
              aria-label="Candidate profile summary"
              value={draft.profileSummary}
              onChange={(e) => patchDraft("profileSummary", e.target.value)}
              className="min-h-[220px] w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary"
              placeholder="Summarize reusable facts, quality signals, interview notes, and share-safe positioning."
            />
            <textarea
              aria-label="Candidate notes"
              value={draft.notes}
              onChange={(e) => patchDraft("notes", e.target.value)}
              className="min-h-[140px] w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary"
              placeholder="Internal recruiter notes that should stay on the candidate record."
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <div className="rounded-xl border border-border bg-surface-alt p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                  Normalized skills
                </div>
                <div className="mt-3">
                  <ChipInput
                    ariaLabel="Candidate profile skills"
                    value={draft.profileSkills}
                    onChange={(next) => patchDraft("profileSkills", next)}
                  />
                </div>
              </div>
              <div className="rounded-xl border border-border bg-surface-alt p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                  Experience highlights
                </div>
                <div className="mt-3">
                  <ChipInput
                    ariaLabel="Candidate profile experience"
                    value={draft.profileExperience}
                    onChange={(next) => patchDraft("profileExperience", next)}
                  />
                </div>
              </div>
            </div>
            {candidate.normalized_profile?.source_of_truth ? (
              <div className="rounded-xl border border-border bg-surface-alt p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                  Source of truth
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {Object.entries(candidate.normalized_profile.source_of_truth).map(([field, sourceOfTruth]) => (
                    <ProvenanceChip key={field} label={`${humanize(field)}: ${humanize(sourceOfTruth)}`} />
                  ))}
                </div>
              </div>
            ) : null}
          </ProcessingSection>

          <ProcessingSection
            title="Core stack"
            description="Use the normalized stack chips recruiters will filter by. Press Enter or comma to add each item."
          >
            <ChipInput
              ariaLabel="Candidate core stack"
              value={draft.coreStack}
              onChange={(next) => patchDraft("coreStack", next)}
            />
            {sourceApplication.data?.skills?.length ? (
              <div className="rounded-xl border border-border bg-surface-alt p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                  Source application skills
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {sourceApplication.data.skills.map((skill) => (
                    <ProvenanceChip key={skill} label={skill} />
                  ))}
                </div>
              </div>
            ) : null}
          </ProcessingSection>

          <ProcessingSection
            title="Commercial details"
            description="Availability, rate, and timing that matter when deciding whether this person is commercially usable."
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <EditablePair
                label="Expected rate"
                value={
                  <input
                    aria-label="Candidate expected rate"
                    value={draft.expectedRate}
                    onChange={(e) => patchDraft("expectedRate", e.target.value)}
                    className={inputClass}
                  />
                }
              />
              <EditablePair
                label="Availability"
                value={
                  <input
                    aria-label="Candidate availability"
                    value={draft.availability}
                    onChange={(e) => patchDraft("availability", e.target.value)}
                    className={inputClass}
                  />
                }
              />
            </div>
            <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
              <EditablePair
                label="Minimum rate"
                value={
                  <input
                    aria-label="Candidate minimum rate"
                    value={draft.minimumRate}
                    onChange={(e) => patchDraft("minimumRate", e.target.value)}
                    className={inputClass}
                    inputMode="numeric"
                  />
                }
              />
              <EditablePair
                label="Rate type"
                value={
                  <input
                    aria-label="Candidate rate type"
                    value={draft.rateType}
                    onChange={(e) => patchDraft("rateType", e.target.value)}
                    className={inputClass}
                    placeholder="hourly, monthly…"
                  />
                }
              />
              <EditablePair
                label="Currency"
                value={
                  <input
                    aria-label="Candidate currency"
                    value={draft.currency}
                    onChange={(e) => patchDraft("currency", e.target.value)}
                    className={inputClass}
                    placeholder="USD"
                  />
                }
              />
              <EditablePair
                label="English level"
                value={
                  <input
                    aria-label="Candidate English level"
                    value={draft.englishLevel}
                    onChange={(e) => patchDraft("englishLevel", e.target.value)}
                    className={inputClass}
                  />
                }
              />
              <EditablePair
                label="Notice period"
                value={
                  <input
                    aria-label="Candidate notice period"
                    value={draft.noticePeriod}
                    onChange={(e) => patchDraft("noticePeriod", e.target.value)}
                    className={inputClass}
                  />
                }
              />
              <EditablePair
                label="Earliest start"
                value={
                  <input
                    aria-label="Candidate earliest start"
                    value={draft.earliestStart}
                    onChange={(e) => patchDraft("earliestStart", e.target.value)}
                    className={inputClass}
                  />
                }
              />
              <EditablePair
                label="Work authorization"
                value={
                  <input
                    aria-label="Candidate work authorization"
                    value={draft.workAuthorization}
                    onChange={(e) => patchDraft("workAuthorization", e.target.value)}
                    className={inputClass}
                  />
                }
              />
              <ReadOnlyFact
                label="Application rate"
                value={formatRateWithCurrency(sourceApplication.data?.expected_rate, sourceApplication.data?.currency)}
              />
              <ReadOnlyFact
                label="Earliest start"
                value={sourceApplication.data?.earliest_start || "—"}
              />
              <ReadOnlyFact
                label="Notice period"
                value={sourceApplication.data?.notice_period || "—"}
              />
              <ReadOnlyFact
                label="Engagement pref"
                value={humanize(sourceApplication.data?.engagement_pref)}
              />
            </div>
          </ProcessingSection>

          <ProcessingSection
            title="Provenance and raw sources"
            description="What this record came from, plus the raw text that exists on current backend contracts."
          >
            <div className="flex flex-wrap gap-2">
              <StatusTag status={source.status} label={source.label} />
              {candidate.source_application_id ? (
                <ProvenanceChip label={`Application #${candidate.source_application_id}`} tone="warning" />
              ) : null}
              {candidate.source_profile_id ? (
                <ProvenanceChip label={`Source profile #${candidate.source_profile_id}`} />
              ) : null}
              {sourceApplication.data?.source_channel ? (
                <ProvenanceChip label={humanize(sourceApplication.data.source_channel)} tone="success" />
              ) : null}
              {sourceApplication.data?.source_campaign ? (
                <ProvenanceChip label={`Campaign: ${sourceApplication.data.source_campaign}`} />
              ) : null}
            </div>
            {sourceApplication.data ? (
              <div className="flex flex-wrap gap-2">
                <Link to={`/admin/applications/${sourceApplication.data.id}`} className="text-sm text-primary hover:underline">
                  Open source application
                </Link>
                {candidate.role_brief_id ? (
                  <Link to={`/admin/role-rail/${candidate.role_brief_id}`} className="text-sm text-primary hover:underline">
                    Open role pipeline #{candidate.role_brief_id}
                  </Link>
                ) : null}
              </div>
            ) : candidate.role_brief_id ? (
              <Link to={`/admin/role-rail/${candidate.role_brief_id}`} className="text-sm text-primary hover:underline">
                Open role pipeline #{candidate.role_brief_id}
              </Link>
            ) : null}
            {sourceSnippets.length ? (
              <div className="grid gap-3">
                {sourceSnippets.map((snippet) => (
                  <SourceSnippetCard key={snippet.label} label={snippet.label} value={snippet.value} />
                ))}
              </div>
            ) : (
              <div className="rounded-xl border border-dashed border-border bg-surface-alt p-4 text-sm text-text-muted">
                No raw application text is attached to this candidate yet.
              </div>
            )}
            {candidate.source_profile_id ? (
              <div className="rounded-xl border border-dashed border-border bg-surface-alt p-4 text-sm text-text-muted">
                Source profile: #{candidate.source_profile_id}. The current candidate API exposes the ID, but not a direct
                sourced-profile detail fetch for raw profile text from this page.
              </div>
            ) : null}
            {/* TODO(frontend-phase-3): switch the sourced-profile placeholder above to real raw-profile content once the backend exposes a direct candidate->profile detail endpoint. */}
          </ProcessingSection>
        </div>

        <aside className="flex min-w-0 flex-col gap-5 xl:sticky xl:top-6">
          <ProcessingSection
            title="Recruiting workflow"
            description="Lifecycle state for the reusable candidate record, separate from role-specific outreach decisions."
          >
            <label className="block">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                Recruiting status
              </span>
              <select
                aria-label="Candidate recruiting status"
                value={draft.status}
                onChange={(e) => patchDraft("status", e.target.value as CandidateStatus)}
                className={inputClass}
              >
                <option value="not_contacted">Not contacted</option>
                <option value="shortlist_ready">Shortlist ready</option>
                <option value="outreach_pending">Outreach pending</option>
                <option value="contacted">Contacted</option>
                <option value="replied">Replied</option>
                <option value="screening">Screening</option>
                <option value="submitted">Submitted</option>
                <option value="rejected">Rejected</option>
                <option value="represented">Represented</option>
                <option value="placed">Placed</option>
                <option value="hold">Hold</option>
              </select>
            </label>
            <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-1">
              <ReadOnlyFact label="Created" value={fmtDate(candidate.created_at)} />
              <ReadOnlyFact label="Last updated" value={fmtDate(candidate.updated_at)} />
            </div>
            <Button variant="danger" onClick={() => setConfirmAction("archive")}>
              Archive candidate
            </Button>
          </ProcessingSection>

          <ProcessingSection
            title="Consent and safe to share"
            description="Treat this as the recruiter-facing gate before any external sharing."
          >
            <div className="grid gap-3 sm:grid-cols-2">
              <EditablePair
                label="Consent scope"
                value={
                  <input
                    aria-label="Candidate consent scope"
                    value={draft.consentScope}
                    onChange={(e) => patchDraft("consentScope", e.target.value)}
                    className={inputClass}
                    placeholder="client_share_anonymized"
                  />
                }
              />
              <EditablePair
                label="Profile status"
                value={
                  <input
                    aria-label="Candidate profile status"
                    value={draft.profileStatus}
                    onChange={(e) => patchDraft("profileStatus", e.target.value)}
                    className={inputClass}
                  />
                }
              />
            </div>
            <label className="block">
              <span className="mb-1.5 block text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                Consent status
              </span>
              <select
                aria-label="Candidate consent status"
                value={draft.consent}
                onChange={(e) => patchDraft("consent", e.target.value as CandidateConsentStatus)}
                className={inputClass}
              >
                <option value="unknown">Unknown</option>
                <option value="requested">Requested</option>
                <option value="approved">Approved</option>
                <option value="denied">Denied</option>
              </select>
            </label>
            <div className="rounded-xl border border-border bg-surface-alt p-4">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                    Share guidance
                  </div>
                  <div className="mt-1 text-sm font-semibold">{shareState.label}</div>
                </div>
                <ProvenanceChip label={shareState.tone === "success" ? "Ready" : shareState.tone === "warning" ? "Hold" : "Blocked"} tone={shareState.tone} />
              </div>
              <p className="mt-2 text-sm text-text-muted">{shareState.detail}</p>
            </div>
            <textarea
              aria-label="Candidate safe to share notes"
              value={draft.safeToShareNotes}
              onChange={(e) => patchDraft("safeToShareNotes", e.target.value)}
              className="min-h-[140px] w-full rounded-xl border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary"
              placeholder="Notes that are safe for buyer-facing sharing, redaction reminders, consent caveats."
            />
          </ProcessingSection>

          <ProcessingSection
            title="CV management"
            description="Current CV asset, optional external URL, and replacement upload."
          >
            <div className="flex flex-col gap-2">
              {cvHref ? (
                <>
                  <a href={cvHref} target="_blank" rel="noopener noreferrer" className={buttonClass({ variant: "secondary", size: "sm" })}>
                    Preview CV
                  </a>
                  <a href={cvHref} download className={buttonClass({ variant: "secondary", size: "sm" })}>
                    Download CV
                  </a>
                </>
              ) : candidate.cv_url ? (
                <CandidateUrlButton value={candidate.cv_url} label="Open CV URL" />
              ) : (
                <p className="text-sm text-text-muted">No CV attached yet.</p>
              )}
            </div>
            {candidate.cv_blob_history?.length ? (
              <div className="rounded-xl border border-border bg-surface-alt p-4">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                  CV history
                </div>
                <div className="mt-2 flex flex-wrap gap-2">
                  {candidate.cv_blob_history.map((blobId) => (
                    <ProvenanceChip
                      key={blobId}
                      label={blobId === candidate.cv_blob_id ? `Blob #${blobId} active` : `Blob #${blobId}`}
                      tone={blobId === candidate.cv_blob_id ? "success" : "neutral"}
                    />
                  ))}
                </div>
              </div>
            ) : null}
            <EditablePair
              label="CV URL"
              value={
                <input
                  aria-label="Candidate CV URL"
                  value={draft.cvUrl}
                  onChange={(e) => patchDraft("cvUrl", e.target.value)}
                  className={inputClass}
                  placeholder="Optional external CV URL"
                />
              }
            />
            <div className="rounded-xl border border-border bg-surface-alt p-4">
              <div className="mb-3 text-[11px] font-semibold uppercase tracking-wider text-text-muted">
                Replace CV
              </div>
              <FileUploader
                value={pendingCvFile}
                onChange={setPendingCvFile}
                accept=".pdf,.doc,.docx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document"
                emptyTitle="Upload or replace candidate CV"
                emptyHint="PDF, DOC, DOCX"
                inputLabel="Candidate CV upload"
              />
              <div className="mt-3 flex flex-col gap-2 sm:flex-row">
                <Button
                  variant="secondary"
                  onClick={() => setPendingCvFile(null)}
                  disabled={!pendingCvFile || uploadCv.isPending}
                >
                  Clear selection
                </Button>
                <Button
                  onClick={onUploadCv}
                  loading={uploadCv.isPending}
                  loadingLabel="Uploading CV…"
                  disabled={!pendingCvFile || update.isPending}
                >
                  Upload selected CV
                </Button>
              </div>
            </div>
          </ProcessingSection>
        </aside>
      </div>
    </div>
  );
}

const inputClass =
  "w-full rounded-md border border-border bg-surface px-3 py-2 text-sm outline-none focus:border-primary";

function ProcessingSection({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: ReactNode;
}) {
  return (
    <section className="rounded-2xl border border-border bg-surface p-5 sm:p-6">
      <div className="mb-4">
        <h2 className="text-base font-semibold">{title}</h2>
        {description ? <p className="mt-1 text-sm text-text-muted">{description}</p> : null}
      </div>
      <div className="flex flex-col gap-4">{children}</div>
    </section>
  );
}

function EditablePair({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-xl bg-surface-alt p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">{label}</div>
      <div className="mt-1 break-words">{value}</div>
    </div>
  );
}

function ReadOnlyFact({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="rounded-xl border border-border bg-surface-alt p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">{label}</div>
      <div className="mt-1 break-words text-sm">{value}</div>
    </div>
  );
}

function SourceSnippetCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface-alt p-4">
      <div className="text-[11px] font-semibold uppercase tracking-wider text-text-muted">{label}</div>
      <p className="mt-2 whitespace-pre-wrap text-sm text-text">{value}</p>
    </div>
  );
}

function MobileEmptyState({ children }: { children: ReactNode }) {
  return (
    <div className="rounded-2xl border border-border bg-surface p-6 text-center text-sm text-text-muted">
      {children}
    </div>
  );
}

function PoolMetric({ label, value }: { label: string; value: string }) {
  return (
    <div className="rounded-xl border border-border bg-surface p-4">
      <div className="text-xs font-semibold uppercase tracking-wider text-text-muted">{label}</div>
      <div className="mt-1 text-xl font-bold">{value}</div>
    </div>
  );
}

function ProvenanceChip({
  label,
  tone = "neutral",
}: {
  label: string;
  tone?: "neutral" | "success" | "warning";
}) {
  const className =
    tone === "success"
      ? "bg-success-soft text-[#166534]"
      : tone === "warning"
        ? "bg-warning-soft text-[#92400e]"
        : "bg-surface-alt text-text-muted";
  return <span className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${className}`}>{label}</span>;
}

function summarizeVisiblePool(candidates: Candidate[]) {
  return candidates.reduce(
    (acc, candidate) => {
      if (candidate.role_brief_id) acc.roleLinked += 1;
      if (candidate.source_profile_id) acc.sourced += 1;
      if (candidate.source_application_id) acc.applications += 1;
      return acc;
    },
    { roleLinked: 0, sourced: 0, applications: 0 },
  );
}

function candidateSource(candidate: Candidate): { label: string; status: string } {
  if (candidate.source_profile_id) return { label: "LinkedIn sourced", status: "sourced_profile" };
  if (candidate.source_application_id) return { label: "Website applicant", status: "application" };
  return { label: "Manual record", status: "manual" };
}

function createCandidateDraft(candidate: Candidate): CandidateDraft {
  return {
    name: candidate.name ?? "",
    linkedinUrl: candidate.linkedin_url ?? "",
    telegramUrl: candidate.telegram_url ?? "",
    email: candidate.email ?? "",
    location: candidate.location ?? "",
    geo: candidate.geo ?? "",
    currentTitle: candidate.current_title ?? "",
    currentCompany: candidate.current_company ?? "",
    expectedRate: candidate.expected_rate ?? "",
    availability: candidate.availability ?? "",
    minimumRate: candidate.minimum_rate != null ? String(candidate.minimum_rate) : "",
    rateType: candidate.rate_type ?? "",
    currency: candidate.currency ?? "",
    englishLevel: candidate.english_level ?? "",
    noticePeriod: candidate.notice_period ?? "",
    earliestStart: candidate.earliest_start ?? "",
    workAuthorization: candidate.work_authorization ?? "",
    safeToShareNotes: candidate.safe_to_share_notes ?? "",
    consentScope: candidate.consent_scope ?? "",
    profileStatus: candidate.profile_status ?? "",
    profileHeadline: candidate.profile_headline ?? candidate.normalized_profile?.headline ?? "",
    profileSummary: candidate.profile_summary ?? candidate.normalized_profile?.summary ?? "",
    profileRoleTitle: candidate.profile_role_title ?? candidate.normalized_profile?.role_title ?? "",
    profileSkills: [...(candidate.profile_skills ?? candidate.normalized_profile?.skills ?? [])],
    profileExperience: [...(candidate.profile_experience ?? candidate.normalized_profile?.experience ?? [])],
    cvUrl: candidate.cv_url ?? "",
    coreStack: [...candidate.core_stack],
    notes: candidate.notes ?? "",
    status: candidate.representation_status as CandidateStatus,
    consent: candidate.consent_status as CandidateConsentStatus,
  };
}

function normalizeOptional(value: string): string | null {
  const trimmed = value.trim();
  return trimmed ? trimmed : null;
}

function normalizeInteger(value: string): number | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  const parsed = Number.parseInt(trimmed, 10);
  return Number.isFinite(parsed) ? parsed : null;
}

function formatRateWithCurrency(rate?: string | null, currency?: string | null): string {
  if (!rate) return "—";
  return currency && !rate.includes(currency) ? `${rate} ${currency}` : rate;
}

function collectSourceSnippets(application: ReturnType<typeof useApplication>["data"]) {
  if (!application) return [] as Array<{ label: string; value: string }>;
  return [
    application.submission_context
      ? { label: "Submission context", value: application.submission_context }
      : null,
    application.cover_letter
      ? { label: "Cover letter", value: application.cover_letter }
      : null,
    application.additional_info
      ? { label: "Additional info", value: application.additional_info }
      : null,
  ].filter((item): item is { label: string; value: string } => item != null);
}

function computeShareState(
  draft: CandidateDraft,
  candidate: Candidate,
  pendingCvFile: File | null,
): { label: string; tone: "neutral" | "success" | "warning"; detail: string } {
  const hasCv = Boolean(candidate.cv_blob_id || normalizeOptional(draft.cvUrl) || pendingCvFile);
  const hasContact = Boolean(normalizeOptional(draft.email) || normalizeOptional(draft.linkedinUrl) || normalizeOptional(draft.telegramUrl));

  if (draft.consent === "denied") {
    return {
      label: "Do not share externally",
      tone: "warning",
      detail: "Consent is explicitly denied. Keep this record internal until the candidate re-approves.",
    };
  }

  if (draft.consent === "approved" && hasCv && hasContact) {
    return {
      label: "Safe to share with buyers",
      tone: "success",
      detail: "Consent is approved and the record has both contact context and a CV asset available.",
    };
  }

  if (draft.consent === "approved") {
    return {
      label: "Consent approved, asset pack incomplete",
      tone: "warning",
      detail: "Consent exists, but add a CV or missing contact details before sharing externally.",
    };
  }

  if (draft.consent === "requested") {
    return {
      label: "Awaiting candidate approval",
      tone: "warning",
      detail: "Do not send this profile outside the team until the consent request is resolved.",
    };
  }

  return {
    label: "Internal processing only",
    tone: "neutral",
    detail: "Consent has not been requested yet. Use the summary and provenance internally, but do not share externally.",
  };
}

type CleanupAction = "archive" | null;

function CleanupConfirm({
  noun,
  itemName,
  loading,
  onCancel,
  onConfirm,
}: {
  noun: string;
  itemName: string;
  loading: boolean;
  onCancel: () => void;
  onConfirm: () => void | Promise<void>;
}) {
  return (
    <div
      role="alertdialog"
      aria-label={"Archive " + noun}
      className="rounded-xl border border-[#fecaca] bg-danger-soft p-4 text-sm"
    >
      <div className="font-semibold text-[#991b1b]">
        Archive this {noun}?
      </div>
      <p className="mt-1 text-[#7f1d1d]">
        This moves {itemName} out of active recruiting while keeping the record available for audit.
      </p>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="ghost" size="sm" onClick={onCancel} disabled={loading}>
          Cancel
        </Button>
        <Button variant="danger" size="sm" onClick={() => void onConfirm()} loading={loading}>
          Confirm archive {noun}
        </Button>
      </div>
    </div>
  );
}
